import { useState } from "react";
import { accountService, homeService } from "@miplatform/api";
import { AutocompleteOption } from "@miplatform/components";
import {
  ALL,
  ENTITY_NAMES,
  HTTP_STATUSES,
  MAX_PAGE_SIZE,
  ORDER_STATUS_DISPLAY,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  AccountType,
  IAccountResponse,
  ICommunityListItemResponse,
  IOrderTemplateResponse,
  OrderStatus,
} from "@miplatform/types";
import { createUUID, sortOnField } from "@miplatform/utils";
import { useOrderTemplates } from "@mibuildweb/hooks";
import {
  ORDERS_SEARCH_FIELDS,
  ORDERS_SEARCH_GROUP_AND_SORT_BY_OPTIONS,
} from "../OrdersSearch.constants";

const getOrderTemplatesUUID = createUUID();
const getCommunitiesUUID = createUUID();
const getCustomersUUID = createUUID();

export const useOrderFilters = () => {
  const [orderTemplateOptions, setOrderTemplateOptions] = useState<
    AutocompleteOption[]
  >([]);

  const [communityOptions, setCommunityOptions] = useState<
    AutocompleteOption[]
  >([]);

  const [customerOptions, setCustomerOptions] = useState<AutocompleteOption[]>(
    []
  );

  const stateManager = useComponentState();

  const { getOrderTemplates } = useOrderTemplates();

  const getCommunities = async (): Promise<void> => {
    stateManager.abort(getCommunitiesUUID);
    await stateManager.run(async () => {
      const { status, data } = await homeService.communities.getAll({
        sort: "name",
      });

      if (status === HTTP_STATUSES.OK) {
        setCommunityOptions([
          { label: ALL, value: ALL },
          ...data.map((community: ICommunityListItemResponse) => ({
            label: community.name,
            value: community.id,
          })),
        ]);
      }
    }, getCommunitiesUUID);
  };

  const getCustomers = async (): Promise<void> => {
    stateManager.abort(getCustomersUUID);
    await stateManager.run(async () => {
      const { status, data } = await accountService.accounts.getAll({
        sort: "name",
        top: MAX_PAGE_SIZE,
        filters: {
          accountType: {
            in: [AccountType.Builder, AccountType.Service],
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        setCustomerOptions([
          { label: ALL, value: ALL },
          ...data.map((customer: IAccountResponse) => ({
            label: customer.name,
            value: customer.id,
          })),
        ]);
      }
    }, getCustomersUUID);
  };

  const getSchedulingOrderTemplates = () => {
    stateManager.abort(getOrderTemplatesUUID);
    stateManager.run(async () => {
      const response = await getOrderTemplates({
        sort: "name",
      });

      if (response.status === HTTP_STATUSES.OK) {
        const templatesOptions: AutocompleteOption[] = [
          { label: ALL, value: ALL },
          ...(response.data as IOrderTemplateResponse[]).map((template) => ({
            label: template.name,
            value: template.id,
            ...template,
          })),
        ];

        setOrderTemplateOptions(templatesOptions);
      }
    }, getOrderTemplatesUUID);
  };

  const orderStatusOptions: AutocompleteOption[] = [
    { label: ALL, value: ALL },
    ...Object.values(OrderStatus)
      .map((status) => ({
        label: ORDER_STATUS_DISPLAY[status],
        value: status,
      }))
      .sort(sortOnField("label")),
  ];

  const dataFilters = {
    [ORDERS_SEARCH_FIELDS.BUILDER_ID]: {
      field: ORDERS_SEARCH_FIELDS.BUILDER_ID,
      label: ENTITY_NAMES.BUILDER,
      data: customerOptions,
    },
    [ORDERS_SEARCH_FIELDS.COMMUNITY_ID]: {
      field: ORDERS_SEARCH_FIELDS.COMMUNITY_ID,
      label: ENTITY_NAMES.COMMUNITY,
      data: communityOptions,
    },
    [ORDERS_SEARCH_FIELDS.ORDER_TEMPLATE_ID]: {
      field: ORDERS_SEARCH_FIELDS.ORDER_TEMPLATE_ID,
      label: ENTITY_NAMES.ORDER_TEMPLATE,
      data: orderTemplateOptions,
    },
    [ORDERS_SEARCH_FIELDS.ORDER_STATUS]: {
      field: ORDERS_SEARCH_FIELDS.ORDER_STATUS,
      label: `${ENTITY_NAMES.ORDER} Status`,
      data: orderStatusOptions,
    },
  };

  const getOrdersSearchDataFilters = () => {
    getSchedulingOrderTemplates();
    getCommunities();
    getCustomers();
  };

  return {
    groupByOptions: ORDERS_SEARCH_GROUP_AND_SORT_BY_OPTIONS,
    sortByOptions: ORDERS_SEARCH_GROUP_AND_SORT_BY_OPTIONS,
    dataFilters,
    isLoading: stateManager.isBusy(),
    getOrdersSearchDataFilters,
  };
};
